import React from 'react'
import { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import nutraLogo from "../../assets/Images/nfpl_hero.png";


export default function Header() {
  const [activeLink, setActiveLink] = useState('HOME');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const closeTimerRef = useRef(null);
  const location = useLocation();

  const navLinks = [
    { name: 'HOME', href: '/' },
    {
      name: 'ABOUT',
      href: '/about-us',
      submenu: [
        { name: 'ABOUT US', href: '/about-us' },
        { name: 'OUR JOURNEY', href: '/our-journey' },
        { name: "DIRECTOR'S WORD", href: '/director-word' },
      ],
    },
    { name: 'PRODUCTS', href: '/products' },
    { name: 'R&D', href: '/research-development' },
    {
      name: 'RESOURCES',
      href: '/blog',
      submenu: [
        { name: 'CAREER', href: '/career' },
        { name: 'BLOG', href: '/blog' },
        { name: 'DOWNLOAD', href: '/download' },
        { name: 'EVENT', href: '/events' },
      ],
    },
    { name: 'CONTACT US', href: '/contact-us' },
  ];

  useEffect(() => {
    const currentPath = location.pathname;

    const parentWithSubmenu = navLinks.find(
      (link) => link.submenu && link.submenu.some((sub) => sub.href === currentPath)
    );
    if (parentWithSubmenu) {
      setActiveLink(parentWithSubmenu.name);
      return;
    }

    const activeNav = navLinks.find((link) => link.href === currentPath);
    if (activeNav) {
      setActiveLink(activeNav.name);
    } else {
      const matchingLink = navLinks.find(
        (link) => currentPath.startsWith(link.href) && link.href !== '/'
      );
      if (matchingLink) {
        setActiveLink(matchingLink.name);
      } else if (currentPath === '/') {
        setActiveLink('HOME');
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileMenu = document.getElementById('mobile-menu');
      const menuButton = document.getElementById('menu-button');
      if (mobileMenuOpen && mobileMenu && !mobileMenu.contains(event.target) && !menuButton?.contains(event.target)) {
        setMobileMenuOpen(false);
      }

      if (openDropdown) {
        const currentRef = dropdownRefs.current[openDropdown];
        if (currentRef && !currentRef.contains(event.target)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, openDropdown]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleNavigation = (linkName) => {
    setActiveLink(linkName);
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileOpenDropdown(null);
  };

  const handleDropdownEnter = (name) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    closeTimerRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header className="w-full bg-white border-b border-gray-200 relative z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-25 gap-x-6 lg:gap-x-10">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center gap-3 -ml-4 sm:-ml-6 lg:-ml-8">
            <Link to="/" onClick={() => handleNavigation('HOME')}>
             <div className="shine">
               <img
                src={nutraLogo}
                alt="Nutralike Logo"
                className="w-auto h-20 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
              />
             </div>
            </Link>
          </div>

          {/* Desktop Navigation + CTA */}
          <div className="hidden md:flex md:items-center gap-5 lg:gap-6">
            <nav className="hidden md:flex items-center justify-center flex-1">
              <div className="flex items-center gap-4 lg:gap-5 xl:gap-7">
                {navLinks.map((link) =>
                  link.submenu ? (
                    <div
                      key={link.name}
                      ref={(el) => (dropdownRefs.current[link.name] = el)}
                      className="relative"
                      onMouseEnter={() => handleDropdownEnter(link.name)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenDropdown((current) => (current === link.name ? null : link.name))
                        }
                        aria-haspopup="true"
                        aria-expanded={openDropdown === link.name}
                        className={`cursor-pointer text-sm font-medium pb-2 transition-colors duration-200 relative whitespace-nowrap inline-flex items-center gap-1 ${
                          activeLink === link.name
                            ? 'text-[#355931]'
                            : 'text-gray-700 hover:text-[#355931]'
                        }`}
                      >
                        {link.name}
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            openDropdown === link.name ? 'rotate-180' : ''
                          }`}
                        />
                        {activeLink === link.name && (
                          <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#355931]"></div>
                        )}
                      </button>

                      {/* Professional Submenu */}
                      <div
                        className={`absolute left-0 -ml-3 top-full pt-6 w-64 transition-all ease-out origin-top-left ${
                          openDropdown === link.name
                            ? 'opacity-100 visible translate-y-0 duration-300'
                            : 'opacity-0 invisible -translate-y-2 pointer-events-none duration-200'
                        }`}
                      >
                        <div className="relative bg-white shadow-[0_8px_30px_-8px_rgba(0,0,0,0.18)] border border-gray-200 overflow-hidden">
                          {/* Solid accent stripe */}
                          <div className="h-[3px] w-full bg-[#355931]"></div>

                          <ul className="py-1.5">
                            {link.submenu.map((sub, idx) => {
                              const isActive = location.pathname === sub.href;
                              return (
                                <li key={sub.name}>
                                  <Link
                                    to={sub.href}
                                    onClick={() => handleNavigation(link.name)}
                                    className={`group relative flex items-center px-5 py-3 text-[13px] font-medium tracking-wide uppercase transition-colors duration-200 ${
                                      isActive
                                        ? 'text-[#355931] bg-gray-50'
                                        : 'text-gray-700 hover:text-[#355931] hover:bg-gray-50'
                                    }`}
                                  >
                                    {/* Left active/hover indicator bar */}
                                    <span
                                      className={`absolute left-0 top-0 bottom-0 w-[3px] bg-[#355931] transition-transform duration-200 origin-top ${
                                        isActive
                                          ? 'scale-y-100'
                                          : 'scale-y-0 group-hover:scale-y-100'
                                      }`}
                                    ></span>
                                    <span className="flex-1">{sub.name}</span>
                                  </Link>
                                  {idx < link.submenu.length - 1 && (
                                    <div className="mx-5 border-b border-gray-100"></div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => handleNavigation(link.name)}
                      className={`cursor-pointer text-sm font-medium pb-2 transition-colors duration-200 relative whitespace-nowrap ${
                        activeLink === link.name
                          ? 'text-[#355931]'
                          : 'text-gray-700 hover:text-[#355931]'
                      }`}
                    >
                      {link.name}
                      {activeLink === link.name && (
                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#355931]"></div>
                      )}
                    </Link>
                  )
                )}
              </div>
            </nav>

            <Link
              to="/inquire-now"
              className="cursor-pointer px-4 py-2 border-1 border-[#355931] text-[#355931] font-semibold rounded-lg hover:bg-[#355931] hover:text-white transition-all duration-200 whitespace-nowrap"
            >
              INQUIRE NOW
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            id="menu-button"
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#355931] z-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <>
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <div
          id="mobile-menu"
          className={`fixed top-0 right-0 h-full w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-2 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <Link to="/" onClick={() => handleNavigation('HOME')}>
                 <div className="shine">
              <img 
              src={nutraLogo}
              alt="Nutralike Logo"
              className="w-auto h-20 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
/>
                 </div>
                </Link>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) =>
                  link.submenu ? (
                    <div key={link.name} className="mx-2">
                      <button
                        type="button"
                        onClick={() =>
                          setMobileOpenDropdown((current) =>
                            current === link.name ? null : link.name
                          )
                        }
                        className={`w-full flex items-center justify-between px-6 py-3 rounded-sm text-base font-medium transition-all duration-200 cursor-pointer ${
                          activeLink === link.name
                            ? 'bg-[#355931]/10 text-[#355931] border-l-4 border-[#355931]'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span>{link.name}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            mobileOpenDropdown === link.name ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          mobileOpenDropdown === link.name ? 'max-h-96 mt-1' : 'max-h-0'
                        }`}
                      >
                        <div className="ml-4 pl-4 border-l-2 border-gray-100 flex flex-col gap-1 py-1">
                          {link.submenu.map((sub) => {
                            const isActive = location.pathname === sub.href;
                            return (
                              <Link
                                key={sub.name}
                                to={sub.href}
                                onClick={() => handleNavigation(link.name)}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                                  isActive
                                    ? 'bg-[#355931]/10 text-[#355931]'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-[#355931]'
                                }`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    isActive ? 'bg-[#355931]' : 'bg-gray-300'
                                  }`}
                                ></span>
                                {sub.name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => handleNavigation(link.name)}
                      className={`text-left px-6 py-3 mx-2 rounded-sm text-base font-medium transition-all duration-200 cursor-pointer ${
                        activeLink === link.name
                          ? 'bg-[#355931] bg-opacity-10 text-white border-l-4 border-[#355931]'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <Link
                to="/inquire-now"
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
                className="w-full block text-center px-6 py-3 bg-[#355931] text-white font-semibold rounded-lg hover:bg-[#2a4727] transition-all duration-200 cursor-pointer"
              >
                INQUIRE NOW
              </Link>
            </div>
          </div>
        </div>
      </>
    </header>
  );
}
